import React, { useEffect, useState } from 'react';
import { AppBar, Paper, Tabs, Tab, Box, Typography, Avatar, Container, Grid, Card, CardContent, Button, Modal, TextField, Alert } from '@mui/material';
import { axiosRun } from "../utils/axiosWrap";
import { config } from "../config";
import MaleIcon from "../images/male_user_icon.png";
import FemaleIcon from "../images/female_user_icon.png";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Barcode from "react-barcode";
import {useTranslation} from "react-i18next";

const LibraryProfile = () => {
    const [value, setValue] = React.useState(0);
    const [isLoad, setIsLoad] = useState(true);
    const [userData, setUserData] = React.useState({});
    const [libraryData, setLibraryData] = useState({ books: [], info: {} });
    const [error, setError] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({ address: '', address2: '', city: '', country: '', phoneNumber: '' });
    const [formError, setFormError] = useState(null);

    const { t } = useTranslation();
    useEffect(() => {
        const getData = async () => {
            try {
                setUserData(await axiosRun(config.apiLinks.getUserInfo, "GET"));
                setLibraryData(await axiosRun(config.apiLinks.getLibraryInfo, 'GET'));
                setIsLoad(false);
            } catch (err) {
                if (err.response?.data?.message === 'error_books_info' || err.response?.data?.message === 'error_member_info') {
                    setError('Вас поки не підключено до бібліотеки');
                } else {
                    setError('Сталася помилка при завантаженні даних');
                }
                setIsLoad(false);
            }
        };
        getData();
    }, []);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFormData({ address: '', address2: '', city: '', country: '', phoneNumber: '' });
        setFormError(null);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRegister = async () => {
        if (!formData.address || !formData.city || !formData.country || !formData.phoneNumber) {
            setFormError(t('library.errorPutAllDataWith'));
            return;
        }
        try {
            setIsLoad(true);
            await axiosRun(config.apiLinks.registerToLibrary, 'POST', formData);
            setUserData(await axiosRun(config.apiLinks.getUserInfo, "GET"));
            setLibraryData(await axiosRun(config.apiLinks.getLibraryInfo, 'GET'));
            setError(null);
            handleCloseModal();
            setIsLoad(false);
        } catch (err) {
            setFormError('Помилка при реєстрації. Спробуйте ще раз.');
            setIsLoad(false);
        }
    };

    return (
        <Paper sx={{ maxWidth: 1200, margin: 'auto', p: 2 }}>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoad}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {error ? (
                <Box mt={2} display="flex" justifyContent="center">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenModal}
                    >
                        Зареєструватись у бібліотеці
                    </Button>
                    <Modal
                        open={openModal}
                        onClose={handleCloseModal}
                        aria-labelledby="register-modal-title"
                        aria-describedby="register-modal-description"
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2
                        }}>
                            <Typography id="register-modal-title" variant="h6" component="h2">
                                Реєстрація в бібліотеці
                            </Typography>
                            {formError && (
                                <Box mt={2}>
                                    <Alert severity="error">{formError}</Alert>
                                </Box>
                            )}
                            <Box mt={2}>
                                <TextField
                                    fullWidth
                                    label={t('library.address1')}
                                    name="address"
                                    value={formData.address}
                                    onChange={handleFormChange}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label={t('library.address2')}
                                    name="address2"
                                    value={formData.address2}
                                    onChange={handleFormChange}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label={t('library.city')}
                                    name="city"
                                    value={formData.city}
                                    onChange={handleFormChange}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label={t('library.country')}
                                    name="country"
                                    value={formData.country}
                                    onChange={handleFormChange}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label={t('library.phone')}
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleFormChange}
                                    margin="normal"
                                />
                                <Box mt={2} display="flex" justifyContent="space-between">
                                    <Button
                                        variant="outlined"
                                        onClick={handleCloseModal}
                                    >
                                       {t('library.cancelReg')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleRegister}
                                    >
                                        {t('library.regNow')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Modal>
                </Box>
            ) : (
                <>
                    <Box>
                        <Tabs value={value} onChange={handleChange} centered>
                            <Tab label="Profile" />
                            <Tab label="Books" />
                        </Tabs>
                    </Box>
                    <Box>
                        <TabPanel value={value} index={0}>
                            <Box display="flex" alignItems="center" flexDirection="column" mt={2}>
                                {userData?.photo ? (
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            width: { xs: 150, md: 300 },
                                            height: { xs: 200, md: 400 },
                                            m: '0 auto'
                                        }}
                                        src={userData.photo}
                                    />
                                ) : (
                                    <Avatar
                                        variant="rounded"
                                        src={userData?.id_PersonSex === 1 ? MaleIcon : FemaleIcon}
                                        sx={{
                                            width: { xs: 150, md: 250 },
                                            height: { xs: 150, md: 250 },
                                            m: 'auto'
                                        }}
                                        imgProps={{ loading: 'lazy' }}
                                    />
                                )}
                                <Typography sx={{ textAlign: 'center' }} variant="h7" mt={2}>
                                    {`${userData?.firstName} ${userData?.lastName} ${userData?.middleName ?? ''}`}
                                </Typography>
                                <Typography sx={{ textAlign: 'center', textWrap: 'wrap' }} variant="body1" mt={1} color="text.secondary">
                                    {libraryData.info?.address}{libraryData.info?.address2 ? `, ${libraryData.info.address2}` : ''}, {libraryData.info?.city}
                                </Typography>
                                <Typography variant="h6" mt={2}>
                                    <Barcode value={libraryData.info?.cardnumber} width={2} />
                                </Typography>

                            </Box>
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <Box mt={2}>
                                <Typography variant="h5" gutterBottom>{t('library.listBooks')}
                                </Typography>
                                <Grid container spacing={2}>
                                    {libraryData.books.map((book, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Card sx={{
                                                boxShadow: 3,
                                                borderRadius: 2,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                maxHeight: 250
                                            }}>
                                                <CardContent sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        color="primary"
                                                        gutterBottom
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            textWrap: 'wrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 4,
                                                            WebkitBoxOrient: 'vertical',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                                        }}
                                                    >
                                                        {book.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            textWrap: 'wrap',
                                                            fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                                        }}
                                                    >
                                                        Автор: {book.author}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </TabPanel>
                    </Box>
                </>
            )}
        </Paper>
    );
};

// TabPanel component for rendering content of each tab
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

export default LibraryProfile;